import { useCallback, useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  FormControl,
  Alert,
  AlertIcon,
  Spinner,
  Text,
  VStack,
  Checkbox,
  Divider,
  Flex,
} from '@chakra-ui/react';
import {
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { LockIcon } from '@chakra-ui/icons';
import StripeElementsProvider from './StripeElementsProvider';

const AddPaymentMethodForm = ({ onSubmit, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [setAsDefault, setSetAsDefault] = useState(true);

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements) return;
    setPaymentError('');
    setIsSubmitting(true);

    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });

      if (result.error) {
        setPaymentError(result.error.message);
      } else {
        await onSubmit({
          paymentMethodId: result.setupIntent.payment_method,
          setAsDefault,
        });
      }
    } catch (error) {
      console.error('Payment submission error:', error);
      setPaymentError(
        'Failed to process your payment method. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [stripe, elements, onSubmit, setAsDefault]);

  return (
    <>
      <ModalBody>
        <VStack spacing={4} align="stretch">
          <Text textStyle="bodyMedium">
            Add a new payment method to your account. This information is
            securely processed by Stripe.
          </Text>
          <FormControl id="payment-element" mt={2}>
            <Flex
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Text textStyle="bodyMedium">Payment information</Text>
              <Flex direction="row" gap={1} align="center">
                <LockIcon w={3} h={3} color="#989898" />
                <Text fontSize="12px" color="#989898" mt={0.5}>
                  Secure payment
                </Text>
              </Flex>
            </Flex>
            <PaymentElement />
            {paymentError && (
              <Alert status="error" mt={2}>
                <AlertIcon h={4} w={4} />
                <Text textStyle="bodySmall">{paymentError}</Text>
              </Alert>
            )}
          </FormControl>

          <FormControl mt={2}>
            <Checkbox
              variant="primary"
              isChecked={setAsDefault}
              onChange={(e) => setSetAsDefault(e.target.checked)}
            >
              <Text textStyle="bodySmall">Set as default payment method</Text>
            </Checkbox>
          </FormControl>
        </VStack>
      </ModalBody>

      <ModalFooter>
        <Flex direction="column" w="100%" gap={4}>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={!stripe || !elements || isSubmitting}
            w="100%"
          >
            Add Payment Method
          </Button>
          <Button variant="link" onClick={onClose} isDisabled={isSubmitting}>
            <Text textStyle="bodySmall">Cancel</Text>
          </Button>
        </Flex>
      </ModalFooter>
    </>
  );
};

const AddPaymentMethodModal = ({
  isOpen,
  onClose,
  onSubmit,
  createSetupIntent,
  setError,
}) => {
  const [setupIntent, setSetupIntent] = useState(null);
  const [setupIntentError, setSetupIntentError] = useState('');
  const [setupIntentLoading, setSetupIntentLoading] = useState(false);

  const getSetupIntent = useCallback(async () => {
    setSetupIntentLoading(true);
    setSetupIntentError('');

    try {
      const intent = await createSetupIntent();
      setSetupIntent(intent);
    } catch (error) {
      console.error('Error creating setup intent:', error);
      setSetupIntentError(
        error.message || 'Failed to initialize payment setup. Please try again.'
      );
      if (setError) {
        setError('Failed to initialize payment setup');
      }
    } finally {
      setSetupIntentLoading(false);
    }
  }, [createSetupIntent, setError]);

  useEffect(() => {
    if (isOpen && !setupIntent) {
      getSetupIntent();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSetupIntentError('');
      setSetupIntent(null);
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent pt={8} pb={6} px={6}>
        <ModalCloseButton />
        <Text textStyle="heading4" textAlign="center">
          Add payment method
        </Text>
        <Divider my="10px" />
        {setupIntentLoading ? (
          <ModalBody>
            <Box textAlign="center" py={4}>
              <Spinner size="md" />
              <Text textStyle="bodyMedium" mt={4}>
                Preparing payment form...
              </Text>
            </Box>
          </ModalBody>
        ) : setupIntentError ? (
          <ModalBody>
            <Alert status="error">
              <AlertIcon h={4} w={4} />
              <Text textStyle="bodySmall">{setupIntentError}</Text>
            </Alert>
          </ModalBody>
        ) : setupIntent ? (
          <StripeElementsProvider clientSecret={setupIntent.clientSecret}>
            <AddPaymentMethodForm onSubmit={onSubmit} onClose={onClose} />
          </StripeElementsProvider>
        ) : null}
      </ModalContent>
    </Modal>
  );
};

export default AddPaymentMethodModal;
